import React, { useState } from 'react';
import styled from 'styled-components';
import { FiX, FiChevronRight, FiChevronLeft, FiCheck, FiAlertCircle } from 'react-icons/fi';
import apiService from '../../services/api';
import logger from '../../utils/logger';
import CSVScoreUpload from './CSVScoreUpload';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Container = styled.div`
  background: var(--background-primary);
  border-radius: 16px;
  width: 90%;
  max-width: 600px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--border-color);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);

  h2 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--text-primary);
  }
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  border-radius: 12px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    color: var(--text-primary);
    background: var(--background-secondary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  background: var(--background-secondary);
  border-bottom: 1px solid var(--border-color);
`;

const StepDot = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s ease;
  flex-shrink: 0;

  background: ${props => props.$active ? 'var(--primary-color)' : 'var(--background-primary)'};
  border: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'var(--border-color)'};
  color: ${props => props.$active ? 'white' : 'var(--text-secondary)'};
  box-shadow: ${props => props.$active ? 'var(--shadow-sm)' : 'none'};

  ${props => props.$completed && `
    background: #4caf50;
    border-color: #4caf50;
    color: white;
  `}
`;

const StepLabel = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: ${props => props.$active ? '600' : '500'};
  ${props => props.$active && 'color: var(--text-primary);'}
`;

const Content = styled.div`
  padding: 1.5rem;
  min-height: 300px;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.875rem;
  letter-spacing: 0.01em;

  .required {
    color: #f44336;
    margin-left: 0.25rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: var(--background-primary);
  color: var(--text-primary);
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;
  resize: vertical;
  min-height: 100px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px var(--focus-ring);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoBox = styled.div`
  background: rgba(33, 150, 243, 0.1);
  border: 1px solid #2196f3;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  color: #1976d2;
  font-size: 0.875rem;
  display: flex;
  gap: 0.75rem;
  line-height: 1.5;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const ErrorBox = styled(InfoBox)`
  background: rgba(244, 67, 54, 0.1);
  border-color: #f44336;
  color: #d32f2f;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 1.5rem;
  border-top: 1px solid var(--border-color);
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
      justify-content: center;
    }
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 12px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-family: 'Inter', sans-serif;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &.primary {
    background: var(--primary-color);
    color: white;
    box-shadow: var(--shadow-sm);

    &:hover:not(:disabled) {
      background: var(--primary-dark);
      transform: translateY(-1px);
      box-shadow: var(--shadow-md);
    }

    &:active:not(:disabled) {
      transform: translateY(0);
    }
  }

  &.secondary {
    background: var(--background-primary);
    border: 1px solid var(--border-color);
    color: var(--text-primary);

    &:hover:not(:disabled) {
      background: var(--background-secondary);
      border-color: var(--border-dark);
    }
  }
`;

const ExternalItemModal = ({ courseId, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [itemType, setItemType] = useState('assignment');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    points: '',
    dueDate: '',
    dueAt: '',
    externalSourceName: '',
    assignmentType: 'other'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdItem, setCreatedItem] = useState(null);
  const [showCSVUpload, setShowCSVUpload] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTypeChange = (type) => {
    setItemType(type);
    setFormData(prev => ({ ...prev, assignmentType: type === 'assignment' ? 'other' : '' }));
  };

  const validateStep1 = () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return false;
    }
    if (!formData.points || isNaN(formData.points) || formData.points < 1 || formData.points > 1000) {
      setError('Points must be a number between 1 and 1000');
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    setError(null);

    if (!validateStep1()) {
      return;
    }

    try {
      setLoading(true);

      const createMethod = itemType === 'assignment'
        ? apiService.createExternalAssignment
        : apiService.createExternalQuiz;

      const payload = {
        title: formData.title,
        points: parseInt(formData.points),
        ...(formData.description && { description: formData.description }),
        ...(formData.externalSourceName && { externalSourceName: formData.externalSourceName }),
        ...(itemType === 'assignment' && formData.dueDate && { dueDate: formData.dueDate }),
        ...(itemType === 'quiz' && formData.dueAt && { dueAt: formData.dueAt }),
        ...(itemType === 'assignment' && { assignmentType: formData.assignmentType })
      };

      const result = await createMethod.call(apiService, parseInt(courseId), payload);

      if (result.success) {
        setCreatedItem(result.data);
        setStep(2);
      } else {
        // Handle validation errors from express-validator
        if (result.errors && Array.isArray(result.errors)) {
          const errorMessages = result.errors.map(e => `${e.param}: ${e.msg}`).join(', ');
          setError(errorMessages);
        } else {
          setError(result.message || 'Failed to create external item');
        }
      }
    } catch (err) {
      logger.error('Error creating external item', err);
      setError(err.message || 'Failed to create external item');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep(1);
    setError(null);
  };

  const handleSkipUpload = () => {
    if (createdItem) {
      onSuccess?.(createdItem);
      onClose?.();
    }
  };

  const handleUploadComplete = (result) => {
    if (createdItem) {
      onSuccess?.(createdItem);
      onClose?.();
    }
  };

  if (showCSVUpload && createdItem) {
    return (
      <CSVScoreUpload
        itemId={createdItem.id}
        itemType={itemType}
        courseId={courseId}
        itemTitle={createdItem.title}
        onUploadComplete={handleUploadComplete}
        onClose={() => {
          onSuccess?.(createdItem);
          onClose?.();
        }}
      />
    );
  }

  return (
    <Overlay onClick={onClose}>
      <Container onClick={(e) => e.stopPropagation()}>
        <Header>
          <h2>{step === 1 ? 'Create External Item' : 'Upload Scores (Optional)'}</h2>
          <CloseButton onClick={onClose} disabled={loading}>
            <FiX size={24} />
          </CloseButton>
        </Header>

        <StepIndicator>
          <StepDot $active={step === 1} $completed={step > 1}>
            {step > 1 ? <FiCheck size={20} /> : '1'}
          </StepDot>
          <StepLabel $active={step === 1}>Create Item</StepLabel>

          <div style={{ flex: 1, height: '2px', background: 'var(--border-color)' }} />

          <StepDot $active={step === 2} $completed={step > 2}>
            {step > 2 ? <FiCheck size={20} /> : '2'}
          </StepDot>
          <StepLabel $active={step === 2}>Upload Scores</StepLabel>
        </StepIndicator>

        <Content>
          {error && (
            <ErrorBox>
              <FiAlertCircle size={20} />
              <span>{error}</span>
            </ErrorBox>
          )}

          {step === 1 ? (
            <>
              <FormGroup>
                <Label>Item Type</Label>
                <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    cursor: 'pointer',
                    padding: '0.625rem 1rem',
                    borderRadius: '12px',
                    border: `1px solid ${itemType === 'assignment' ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    background: itemType === 'assignment' ? 'var(--background-secondary)' : 'var(--background-primary)',
                    transition: 'all 0.2s ease',
                    fontWeight: itemType === 'assignment' ? '600' : '500',
                    fontSize: '0.875rem',
                    color: itemType === 'assignment' ? 'var(--primary-color)' : 'var(--text-primary)'
                  }}>
                    <input
                      type="radio"
                      value="assignment"
                      checked={itemType === 'assignment'}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      disabled={loading}
                      style={{ margin: 0, cursor: 'pointer' }}
                    />
                    Assignment
                  </label>
                  <label style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '0.5rem', 
                    cursor: 'pointer',
                    padding: '0.625rem 1rem',
                    borderRadius: '12px',
                    border: `1px solid ${itemType === 'quiz' ? 'var(--primary-color)' : 'var(--border-color)'}`,
                    background: itemType === 'quiz' ? 'var(--background-secondary)' : 'var(--background-primary)',
                    transition: 'all 0.2s ease',
                    fontWeight: itemType === 'quiz' ? '600' : '500',
                    fontSize: '0.875rem',
                    color: itemType === 'quiz' ? 'var(--primary-color)' : 'var(--text-primary)'
                  }}>
                    <input
                      type="radio"
                      value="quiz"
                      checked={itemType === 'quiz'}
                      onChange={(e) => handleTypeChange(e.target.value)}
                      disabled={loading}
                      style={{ margin: 0, cursor: 'pointer' }}
                    />
                    Quiz
                  </label>
                </div>
              </FormGroup>

              <FormGroup>
                <Label>
                  Title <span className="required">*</span>
                </Label>
                <Input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Canvas Midterm Exam"
                  disabled={loading}
                  maxLength="255"
                />
              </FormGroup>

              <FormGroup>
                <Label>Max Points <span className="required">*</span></Label>
                <Input
                  type="number"
                  name="points"
                  value={formData.points}
                  onChange={handleInputChange}
                  placeholder="e.g., 100"
                  min="1"
                  max="1000"
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>
                  Due Date
                  {itemType === 'assignment' && ' (Assignment)'}
                  {itemType === 'quiz' && ' (Quiz)'}
                </Label>
                <Input
                  type="date"
                  name={itemType === 'assignment' ? 'dueDate' : 'dueAt'}
                  value={itemType === 'assignment' ? formData.dueDate : formData.dueAt}
                  onChange={handleInputChange}
                  disabled={loading}
                />
              </FormGroup>

              <FormGroup>
                <Label>External Source Name</Label>
                <Input
                  type="text"
                  name="externalSourceName"
                  value={formData.externalSourceName}
                  onChange={handleInputChange}
                  placeholder="e.g., Canvas, Paper Exam, etc."
                  disabled={loading}
                  maxLength="255"
                />
              </FormGroup>

              {itemType === 'assignment' && (
                <FormGroup>
                  <Label>Assignment Type</Label>
                  <Select
                    name="assignmentType"
                    value={formData.assignmentType}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <option value="essay">Essay</option>
                    <option value="exam">Exam</option>
                    <option value="discussion">Discussion</option>
                    <option value="project">Project</option>
                    <option value="other">Other</option>
                  </Select>
                </FormGroup>
              )}

              <FormGroup>
                <Label>Description</Label>
                <TextArea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Optional description..."
                  disabled={loading}
                />
              </FormGroup>

              <InfoBox>
                <FiAlertCircle size={20} />
                <span>
                  This external item will be available in your gradebook and can be
                  assigned to rubric categories for grade calculation.
                </span>
              </InfoBox>
            </>
          ) : (
            <>
              <InfoBox>
                <FiCheck size={20} style={{ color: '#4caf50' }} />
                <span>
                  <strong>{createdItem?.title}</strong> has been created successfully!
                </span>
              </InfoBox>

              <FormGroup style={{ marginBottom: 0 }}>
                <Label style={{ marginBottom: '1rem' }}>Would you like to upload scores now?</Label>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0 }}>
                  You can upload a CSV file with student scores now, or skip this step and upload scores later.
                </p>
              </FormGroup>
            </>
          )}

          <ButtonGroup>
            {step === 2 && (
              <Button
                className="secondary"
                onClick={handleBack}
                disabled={loading}
              >
                <FiChevronLeft size={18} />
                Back
              </Button>
            )}
            <Button
              className="secondary"
              onClick={step === 1 ? onClose : handleSkipUpload}
              disabled={loading}
            >
              {step === 1 ? 'Cancel' : 'Skip'}
            </Button>
            {step === 1 && (
              <Button
                className="primary"
                onClick={handleNext}
                disabled={loading}
              >
                Next <FiChevronRight size={18} />
              </Button>
            )}
            {step === 2 && (
              <Button
                className="primary"
                onClick={() => setShowCSVUpload(true)}
                disabled={loading}
              >
                Upload Scores
              </Button>
            )}
          </ButtonGroup>
        </Content>
      </Container>
    </Overlay>
  );
};

export default ExternalItemModal;
